import { CardioIcon, CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { GymIcon, GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { WeightliftingIcon, WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { word } from "@modules/core/utils/i18n"
import { HUText } from "@modules/ui/components/huText"
import { ThemeMode } from "@modules/ui/styles/theme"
import { useCallback } from "react"
import { useTheme } from "styled-components"

export enum SessionTagEnum {
  gymnasticTag = "gymnasticTag",
  cardioTag = "cardioTag",
  weightliftingTag = "weightliftingTag",
}

type SessionTagInputProps = {
  selectedTags: { key: SessionTagEnum; value: boolean }[]
  onChangeSelectedTags: (tag: string, value: boolean) => void
} & React.HTMLAttributes<HTMLDivElement>

export const SessionTagInput: React.FC<SessionTagInputProps> = ({ selectedTags, onChangeSelectedTags, ...props }) => {
  const isTagSelected = useCallback(
    (tag: SessionTagEnum) => selectedTags.some((t) => t.key === tag && t.value),
    [selectedTags],
  )

  return (
    <div className="flex w-full justify-content-between align-items-stretch card gap-4 p-3" {...props}>
      {Object.values(SessionTagEnum).map((tag) => (
        <SessionTag
          key={tag}
          tag={tag as SessionTagEnum}
          isSelected={isTagSelected(tag as SessionTagEnum)}
          onSelect={() => onChangeSelectedTags(tag.toString(), !isTagSelected(tag as SessionTagEnum))}
        />
      ))}
    </div>
  )
}

type SessionTagProps = {
  tag: SessionTagEnum
  isSelected: boolean
  onSelect: () => void
}

const SessionTag: React.FC<SessionTagProps> = ({ tag, isSelected, onSelect }) => {
  const theme = useTheme()

  const themeSpecificClass =
    theme.mode === ThemeMode.ATHLETE
      ? `${isSelected ? "bg-neutralColor-0" : "border-300"} hover:surface-200 hover:border-300`
      : `${isSelected ? "border-primary" : "border-300"} hover:surface-100`

  return (
    <div
      className={`flex flex-column flex-1 align-items-center justify-content-between gap-1 py-3 rounded-big-radius cursor-pointer transition-duration-200 transition-all border-100 border-1 
        ${themeSpecificClass}`}
      onClick={onSelect}
    >
      {tag === SessionTagEnum.gymnasticTag ? (
        <>
          {isSelected ? <GymSelectedIcon color="" width={25} /> : <GymIcon color={""} width={25} />}
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("program.planning.create.gymnastique")}
          </HUText>
        </>
      ) : tag === SessionTagEnum.weightliftingTag ? (
        <>
          {isSelected ? (
            <WeightliftingSelectedIcon color={""} width={25} />
          ) : (
            <WeightliftingIcon color={""} width={25} />
          )}
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("program.planning.create.weightlifting")}
          </HUText>
        </>
      ) : tag === SessionTagEnum.cardioTag ? (
        <>
          {isSelected ? <CardioSelectedIcon color={""} width={25} /> : <CardioIcon color={""} width={25} />}
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("program.planning.create.cardio")}
          </HUText>
        </>
      ) : null}
    </div>
  )
}
